/**
 * synotis-support#21 — Rapports › Projets : colonnes masquables.
 *
 * JS pur (sans React) : testé par `api/scripts/testProjectsReportColumns.ts`.
 * On mémorise les colonnes MASQUÉES, pas les visibles : une colonne ajoutée
 * plus tard s'affiche d'office. Persistance durable sur le navigateur
 * (localStorage, comme le choix Heures/Jours) jusqu'à « Réinitialiser ».
 * Toute valeur illisible retombe sur « tout affiché », sans erreur.
 *
 * L'export CSV ne dépend pas de ce choix : il exporte toujours toutes les
 * colonnes (`csvColumns` de la vue).
 */

export const COLUMNS_STORAGE_KEY = 'synolog.reports.projects.hiddenColumns';

/** Ordre d'affichage ; `key` = champ de tri de la vue. */
export const PROJECTS_REPORT_COLUMNS = [
  { key: 'name', label: 'Projet', hideable: false },
  { key: 'client', label: 'Client', hideable: true },
  { key: 'pm', label: 'PM', hideable: true },
  { key: 'budget', label: 'Budget', hideable: true },
  { key: 'consumed', label: 'Consommé', hideable: true },
  { key: 'consumptionPct', label: '% consommé', hideable: true },
  { key: 'remaining', label: 'Reste', hideable: true },
  { key: 'logged30d', label: 'Saisi 30 j', hideable: true },
  { key: 'planned', label: 'Planifié restant', hideable: true },
  { key: 'people', label: 'Actifs 30 j', hideable: true },
  { key: 'last', label: 'Dernière saisie', hideable: true },
  { key: 'switchDate', label: 'Bascule prévue', hideable: true },
];

const HIDEABLE = new Set(PROJECTS_REPORT_COLUMNS.filter((c) => c.hideable).map((c) => c.key));

/** Liste de clés masquables connues, sans doublon ; tout le reste est ignoré. */
function sanitize(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const k of value) {
    if (typeof k === 'string' && HIDEABLE.has(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

function defaultStorage() {
  return typeof window === 'undefined' ? undefined : window.localStorage;
}

export function loadHiddenColumns(storage = defaultStorage()) {
  try {
    return sanitize(JSON.parse(storage.getItem(COLUMNS_STORAGE_KEY) ?? 'null'));
  } catch {
    return [];
  }
}

export function saveHiddenColumns(hidden, storage = defaultStorage()) {
  try {
    const clean = sanitize(hidden);
    if (clean.length === 0) storage.removeItem(COLUMNS_STORAGE_KEY);
    else storage.setItem(COLUMNS_STORAGE_KEY, JSON.stringify(clean));
  } catch {
    /* préférence non mémorisée, sans impact */
  }
}

/** Réaffiche tout et efface la préférence. */
export function resetHiddenColumns(storage = defaultStorage()) {
  saveHiddenColumns([], storage);
  return [];
}

/** Masque ou réaffiche `key` ; une colonne non masquable reste affichée. */
export function toggleHiddenColumn(hidden, key) {
  const clean = sanitize(hidden);
  if (!HIDEABLE.has(key)) return clean;
  return clean.includes(key) ? clean.filter((k) => k !== key) : [...clean, key];
}

/** Colonnes de données affichées, dans l'ordre du tableau. */
export function visibleColumns(hidden) {
  const set = new Set(sanitize(hidden));
  return PROJECTS_REPORT_COLUMNS.filter((c) => !set.has(c.key));
}

/** Largeur des lignes Chargement/Erreur/Vide : colonnes visibles + case à cocher. */
export function tableColSpan(hidden) {
  return visibleColumns(hidden).length + 1;
}
